/**
 * GLSL for the fusion compositor (fusionCompositor.ts): one fullscreen draw
 * blends up to `maxStations` station textures per-pixel into the output
 * canvas. Targets GLSL ES 1.00 (attribute/varying/texture2D/gl_FragColor) so
 * the exact same source runs unmodified on both a "webgl" and a "webgl2"
 * context (WebGL2 still accepts ES 1.00 shaders via its compatibility
 * profile as long as no `#version 300 es` pragma is used) — one code path
 * instead of two shader variants to keep in sync.
 *
 * Per-station blocks are unrolled at generation time (a JS loop building the
 * string, not a GLSL loop over a sampler array): WebGL1/GLSL ES 1.00 only
 * guarantees CONSTANT-expression indexing into a sampler array in strict
 * implementations, so a `for` loop indexing `u_tex[i]` is a real portability
 * risk with only 8 stations to handle — an unrolled sequence of named
 * uniforms (`u_tex0`..`u_tex7`) sidesteps that class of bug entirely at the
 * cost of a longer (but still small) generated source.
 */
import type { BlendMode } from "./fusionCompositor";

export const BLEND_MODE_INDEX: Record<BlendMode, number> = {
  nearest: 0,
  max: 1,
  "owner-fallback": 2,
};

export const VERTEX_SHADER_SOURCE = `
attribute vec2 a_position;
varying vec2 v_uv;
void main() {
  // v_uv: (0,0) at the window's NW corner, (1,1) at SE — matches
  // fusionGeo.ts's worldPositionForUv/stationUvTransform convention.
  v_uv = vec2((a_position.x + 1.0) * 0.5, (1.0 - a_position.y) * 0.5);
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

function stationUniformBlock(i: number): string {
  return `
uniform sampler2D u_tex${i};
uniform vec4 u_uv${i};      // (scaleX, scaleY, offsetX, offsetY) — window UV -> this station's UV
uniform vec2 u_center${i};  // station center, EPSG:3857 meters
uniform float u_active${i}; // 1.0 if bound & should be considered this frame`;
}

function stationEvalBlock(i: number): string {
  // Per-station intermediates: is this pixel within the station's own
  // texture footprint (inBounds), and does that station actually have USABLE
  // data there (valid — raw > 0 excludes the shared nodata sentinel, same
  // encoding client/src/palette.ts always used for per-station frames;
  // raw >= u_floor additionally excludes sub-floor returns, per-station,
  // BEFORE any blend rule below runs — see fusionCompositor.ts's doc comment
  // on why flooring has to happen here rather than after blending).
  // flooredRaw is the value blend rules should actually read: 0 (== nodata)
  // whenever this station's own sample isn't valid, so owner-fallback
  // (ownerAccumBlock below) can tell "owner has no usable data" from "owner
  // has real data" using the same raw>0.0 check it already used for true
  // nodata, without a separate floor-aware branch.
  return `
  vec2 uv${i} = v_uv * u_uv${i}.xy + u_uv${i}.zw;
  bool inBounds${i} = u_active${i} > 0.5 && uv${i}.x >= 0.0 && uv${i}.x <= 1.0 && uv${i}.y >= 0.0 && uv${i}.y <= 1.0;
  float raw${i} = inBounds${i} ? texture2D(u_tex${i}, uv${i}).r : 0.0;
  bool valid${i} = inBounds${i} && raw${i} > 0.0 && raw${i} >= u_floor;
  float flooredRaw${i} = valid${i} ? raw${i} : 0.0;
  float dist${i} = inBounds${i} ? distance(world, u_center${i}) : 1.0e20;`;
}

function nearestAccumBlock(i: number): string {
  return `
  if (valid${i} && dist${i} < nearestDist) { nearestDist = dist${i}; nearestRaw = raw${i}; nearestFound = true; }`;
}

function maxAccumBlock(i: number): string {
  return `
  if (valid${i}) { maxRaw = max(maxRaw, raw${i}); maxFound = true; }`;
}

function ownerAccumBlock(i: number): string {
  // Ownership is by geographic nearness among COVERING stations regardless
  // of whether that owner has USABLE data here (inBounds${i}, not
  // valid${i}) — the "owner-nodata falls back to any-station-max" rule from
  // the (removed) server-mosaic design (workers/CONTRACTS.md), now applied
  // client-side. The VALUE taken from that owner is flooredRaw${i}, not
  // raw${i}: a station whose only "data" at this pixel is sub-floor
  // noise/clutter must lose to the fallback exactly like true nodata does
  // (below, `ownerRaw > 0.0`), not win outright just because it happened to
  // be the nearest covering station — that was the bug (floor applied AFTER
  // blending, or not at all, let a nearer station's noise beat a farther
  // station's real precip return).
  return `
  if (inBounds${i} && dist${i} < ownerDist) { ownerDist = dist${i}; ownerRaw = flooredRaw${i}; ownerFound = true; }`;
}

export function buildFragmentShaderSource(maxStations: number): string {
  const uniforms = Array.from({ length: maxStations }, (_, i) => stationUniformBlock(i)).join("\n");
  const evals = Array.from({ length: maxStations }, (_, i) => stationEvalBlock(i)).join("\n");
  const nearestAccum = Array.from({ length: maxStations }, (_, i) => nearestAccumBlock(i)).join("\n");
  const maxAccum = Array.from({ length: maxStations }, (_, i) => maxAccumBlock(i)).join("\n");
  const ownerAccum = Array.from({ length: maxStations }, (_, i) => ownerAccumBlock(i)).join("\n");

  return `
precision highp float;
varying vec2 v_uv;

uniform vec2 u_windowOrigin; // (minX, maxY) meters — NW corner
uniform vec2 u_windowSize;   // (width, height) meters
uniform int u_blendMode;     // 0=nearest, 1=max, 2=owner-fallback (fusionShader.ts BLEND_MODE_INDEX)
uniform sampler2D u_palette; // 256x1 RGBA LUT, same table as palette.ts's PALETTE_LUT
uniform float u_floor;       // normalized raw-byte display floor (raw/255) — see fusionCompositor.ts's PRECIP_FLOOR_RAW/NO_FLOOR_RAW
${uniforms}

void main() {
  vec2 world = vec2(u_windowOrigin.x + v_uv.x * u_windowSize.x, u_windowOrigin.y - v_uv.y * u_windowSize.y);
${evals}

  float nearestDist = 1.0e20; float nearestRaw = 0.0; bool nearestFound = false;
${nearestAccum}

  float maxRaw = 0.0; bool maxFound = false;
${maxAccum}

  float ownerDist = 1.0e20; float ownerRaw = 0.0; bool ownerFound = false;
${ownerAccum}
  // Owner-with-fallback: the geographically nearest COVERING station wins;
  // if that owner has no data here, fall back to the max of whichever other
  // covering stations DO (maxRaw already excludes nodata contributors).
  float ownerFallbackRaw = (ownerRaw > 0.0) ? ownerRaw : maxRaw;
  bool ownerFallbackFound = ownerFound;

  float outRaw = 0.0; bool found = false;
  if (u_blendMode == 0) { outRaw = nearestRaw; found = nearestFound; }
  else if (u_blendMode == 1) { outRaw = maxRaw; found = maxFound; }
  else { outRaw = ownerFallbackRaw; found = ownerFallbackFound; }

  float idx = found ? outRaw : 0.0; // 0 = the palette's own transparent nodata entry
  gl_FragColor = texture2D(u_palette, vec2((idx * 255.0 + 0.5) / 256.0, 0.5));
}
`;
}

export function compileShader(gl: WebGLRenderingContext | WebGL2RenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type);
  if (!shader) throw new Error("gl.createShader failed");
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`shader compile failed: ${log ?? "(no log)"}`);
  }
  return shader;
}

export function compileProgram(gl: WebGLRenderingContext | WebGL2RenderingContext, vsSource: string, fsSource: string): WebGLProgram {
  const vs = compileShader(gl, gl.VERTEX_SHADER, vsSource);
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, fsSource);
  const program = gl.createProgram();
  if (!program) throw new Error("gl.createProgram failed");
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(program);
    gl.deleteProgram(program);
    throw new Error(`program link failed: ${log ?? "(no log)"}`);
  }
  gl.deleteShader(vs);
  gl.deleteShader(fs);
  return program;
}
